import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  LogOut, 
  Lock,
  Download,
  Eye,
  EyeOff,
  Filter,
  Loader2,
  TrendingUp,
  TrendingDown,
  Package,
  Boxes,
  CheckSquare,
  Square,
  Users,
  Percent,
  Merge,
  AlertTriangle,
  X,
  UserPlus,
  FolderEdit,
  Building2,
  Undo2,
  Redo2,
  Trash2,
  Plus,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { 
  PIPELINES,
  MONTHS,
  ACCOUNT_MANAGERS,
  convertToCSV,
  downloadCSV
} from '../data/mockData';
import { customerApi, lookupApi, masterApi, lockApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatLakhs, formatCrores, formatIndianNumber } from '../utils/formatCurrency';
import { MergePipelineDialog, DivisionChangeDialog, TransferDialog } from './budget/BudgetDialogs';
import { toast } from 'sonner';

// Maximum history size for undo/redo
const MAX_HISTORY_SIZE = 50;

export const BudgetEntry = () => {
  const { entryId: rawEntryId } = useParams();  // This is actually composite_id (URL encoded)
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Decode the composite_id from URL
  const compositeId = rawEntryId ? decodeURIComponent(rawEntryId) : null;
  
  // Customer and loading state
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if locked
  const [isLocked, setIsLocked] = useState(false);
  const [lockedByUser, setLockedByUser] = useState(null); // { name, emp_id } of lock holder
  const lockHeartbeatRef = useRef(null);
  
  // State - Merged data
  const [budgetData, setBudgetData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Undo/Redo history
  const [undoStack, setUndoStack] = useState([]); // Stack of previous states
  const [redoStack, setRedoStack] = useState([]); // Stack of undone states
  
  // Lookup data
  const [pipelines, setPipelines] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [productCatalog, setProductCatalog] = useState([]); // Full catalog for the division
  const [accountManagersList, setAccountManagersList] = useState([]);
  
  // Load customer and products data
  useEffect(() => {
    const fetchData = async () => {
      if (!compositeId || authLoading) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch customer using composite_id (cust_code|division|emp_id)
        const customerData = await customerApi.getProducts(compositeId);
        const custInfo = customerData.customer;
        const customerDivision = custInfo.division;
        
        // Now fetch lookups filtered by division
        const [pipelinesData, productGroupsData, catalogData, managersData] = await Promise.all([
          lookupApi.getPipelines().catch(() => PIPELINES.map(p => p.name)),
          lookupApi.getProductGroups(customerDivision).catch(() => []), // No fallback - show empty if no data
          lookupApi.getProducts(customerDivision).catch(() => []), // Full product catalog for division
          lookupApi.getAccountManagers().catch(() => ACCOUNT_MANAGERS)
        ]);
        
        // Set customer info
        setCustomer({
          id: custInfo.composite_id,  // Use composite_id as primary identifier
          compositeId: custInfo.composite_id,
          entryIds: custInfo.entry_ids || [],  // All entry_ids for this customer
          pipelines: custInfo.pipelines || [],  // All pipelines for this customer
          name: custInfo.name,
          code: custInfo.cust_code,
          division: custInfo.division,
          accountManager: custInfo.account_manager_name,
          accountManagerId: custInfo.account_manager_emp_id,
          approved: custInfo.approved || custInfo.status === 1,
          status: custInfo.status,
          lastYearTotal: custInfo.last_year_total || 0,
          thisYearTotal: custInfo.this_year_total || 0
        });
        
        // Check if locked (approved)
        if (custInfo.approved || custInfo.status === 1) {
          setIsLocked(true);
        } else if (searchParams.get('locked') === 'true') {
          setIsLocked(true);
        } else {
          // Try to acquire lock for editing
          try {
            const lockResult = await lockApi.acquire(custInfo.composite_id);
            if (!lockResult.acquired) {
              setIsLocked(true);
              setLockedByUser({
                name: lockResult.locked_by_name,
                emp_id: lockResult.locked_by_emp_id,
              });
            }
          } catch (lockErr) {
            console.error('Lock acquire failed:', lockErr);
          }
        }
        
        // Transform products to UI format
        const isEmptyValue = (val) => !val || val === 'NA' || val === 'N/A' || val === '-' || val.trim?.() === '';
        
        const products = customerData.products.map((p, index) => ({
          id: `${p.pipeline}-${p.product_code || 'pg'}-${index}`,
          entryId: p.entry_id,  // Include entry_id for reference
          compositeId: p.composite_id || custInfo.composite_id,  // Include composite_id for MongoDB saves
          productCode: (p.product_code || '').trim(),  // Store product_code for save
          pipeline: p.pipeline,
          stage: p.stage || '-',
          productGroup: isEmptyValue(p.product_group) ? '' : p.product_group,
          productName: isEmptyValue(p.product_name) ? '' : p.product_name,
          list1: isEmptyValue(p.list1) ? '' : p.list1,
          list2: isEmptyValue(p.list2) ? '' : p.list2,
          rate: p.rate !== undefined && p.rate !== null ? p.rate : 0,
          isProductNameRow: !isEmptyValue(p.product_name),
          isEmptyRow: isEmptyValue(p.product_group),
          color: p.color || 'default',
          isRepeatDeal: p.is_repeat_deal || p.pipeline === 'REPEAT DEAL',  // Flag for visual distinction
          
          // THIS YEAR data (budget being planned - editable)
          aprQty: p.apr_qty || 0, aprAmount: p.apr_amount || 0,
          mayQty: p.may_qty || 0, mayAmount: p.may_amount || 0,
          junQty: p.jun_qty || 0, junAmount: p.jun_amount || 0,
          julQty: p.jul_qty || 0, julAmount: p.jul_amount || 0,
          augQty: p.aug_qty || 0, augAmount: p.aug_amount || 0,
          sepQty: p.sep_qty || 0, sepAmount: p.sep_amount || 0,
          octQty: p.oct_qty || 0, octAmount: p.oct_amount || 0,
          novQty: p.nov_qty || 0, novAmount: p.nov_amount || 0,
          decQty: p.dec_qty || 0, decAmount: p.dec_amount || 0,
          janQty: p.jan_qty || 0, janAmount: p.jan_amount || 0,
          febQty: p.feb_qty || 0, febAmount: p.feb_amount || 0,
          marQty: p.mar_qty || 0, marAmount: p.mar_amount || 0,
          
          // LAST YEAR data (historical reference - read-only from MySQL)
          lyAprQty: p.ly_apr_qty || 0, lyAprAmount: p.ly_apr_amount || 0,
          lyMayQty: p.ly_may_qty || 0, lyMayAmount: p.ly_may_amount || 0,
          lyJunQty: p.ly_jun_qty || 0, lyJunAmount: p.ly_jun_amount || 0,
          lyJulQty: p.ly_jul_qty || 0, lyJulAmount: p.ly_jul_amount || 0,
          lyAugQty: p.ly_aug_qty || 0, lyAugAmount: p.ly_aug_amount || 0,
          lySepQty: p.ly_sep_qty || 0, lySepAmount: p.ly_sep_amount || 0,
          lyOctQty: p.ly_oct_qty || 0, lyOctAmount: p.ly_oct_amount || 0,
          lyNovQty: p.ly_nov_qty || 0, lyNovAmount: p.ly_nov_amount || 0,
          lyDecQty: p.ly_dec_qty || 0, lyDecAmount: p.ly_dec_amount || 0,
          lyJanQty: p.ly_jan_qty || 0, lyJanAmount: p.ly_jan_amount || 0,
          lyFebQty: p.ly_feb_qty || 0, lyFebAmount: p.ly_feb_amount || 0,
          lyMarQty: p.ly_mar_qty || 0, lyMarAmount: p.ly_mar_amount || 0,
          
          // This Year Totals
          totalQty: MONTHS.reduce((sum, m) => sum + (p[`${m.key}_qty`] || 0), 0),
          totalAmount: MONTHS.reduce((sum, m) => sum + (p[`${m.key}_amount`] || 0), 0),
          
          // Last Year Totals
          lyTotalQty: MONTHS.reduce((sum, m) => sum + (p[`ly_${m.key}_qty`] || 0), 0),
          lyTotalAmount: MONTHS.reduce((sum, m) => sum + (p[`ly_${m.key}_amount`] || 0), 0),
          
          // Original data for saving
          _original: p
        }));
        
        setBudgetData(products);
        setPipelines(pipelinesData.length ? pipelinesData : PIPELINES.map(p => p.name));
        setProductGroups(productGroupsData || []); // No fallback - empty if no data
        setProductCatalog(Array.isArray(catalogData) ? catalogData : []);
        setAccountManagersList(Array.isArray(managersData) ? managersData : ACCOUNT_MANAGERS);
        
      } catch (err) {
        console.error('Failed to fetch budget data:', err);
        setError(err.message);
        toast.error('Failed to load budget data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [compositeId, searchParams, authLoading]);
  
  // Lock heartbeat - keep lock alive every 60 seconds
  useEffect(() => {
    if (!compositeId || isLocked || lockedByUser) return;
    lockHeartbeatRef.current = setInterval(async () => {
      try {
        await lockApi.heartbeat(compositeId);
      } catch (err) {
        console.error('Lock heartbeat failed:', err);
      }
    }, 60000);
    return () => {
      if (lockHeartbeatRef.current) clearInterval(lockHeartbeatRef.current);
    };
  }, [compositeId, isLocked, lockedByUser]);

  // Release lock on unmount / page leave
  useEffect(() => {
    if (!compositeId) return;
    const releaseLock = async () => {
      if (!lockedByUser) {
        try { await lockApi.release(compositeId); } catch {}
      }
    };
    const handleBeforeUnload = () => {
      // Use sendBeacon for unload - attach token as query param since we can't set headers
      const token = sessionStorage.getItem('auth_token');
      if (!token) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/locks/release`;
      const blob = new Blob([JSON.stringify({ composite_id: compositeId })], { type: 'application/json' });
      // sendBeacon doesn't support custom headers, so we'll just try fetch with keepalive
      fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ composite_id: compositeId }),
        keepalive: true,
      }).catch(() => {});
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      releaseLock();
    };
  }, [compositeId, lockedByUser]);

  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // View mode toggles
  const [hideAmountRate, setHideAmountRate] = useState(false);
  const [hideQtyRate, setHideQtyRate] = useState(false);
  const [hideLastYear, setHideLastYear] = useState(true);  // Default: hide LY data
  
  // Filters - restore from sessionStorage
  const [pipelineFilter, setPipelineFilter] = useState(() => sessionStorage.getItem('be_pipelineFilter') || 'all');
  const [productGroupFilter, setProductGroupFilter] = useState(() => sessionStorage.getItem('be_productGroupFilter') || 'all');
  const [productNameFilter, setProductNameFilter] = useState(() => sessionStorage.getItem('be_productNameFilter') || 'all');
  const [list1Filter, setList1Filter] = useState(() => sessionStorage.getItem('be_list1Filter') || 'all');
  const [list2Filter, setList2Filter] = useState(() => sessionStorage.getItem('be_list2Filter') || 'all');
  const [stageFilter, setStageFilter] = useState(() => sessionStorage.getItem('be_stageFilter') || 'all');
  const [showNewDealSuspects, setShowNewDealSuspects] = useState(() => sessionStorage.getItem('be_showNewDealSuspects') === 'true');

  // Bulk action dialogs
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showPercentDialog, setShowPercentDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [percentChange, setPercentChange] = useState('');
  const [mergeTargetRow, setMergeTargetRow] = useState('');
  
  // Division and Transfer dialogs for BudgetEntry header
  const [showDivisionDialog, setShowDivisionDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [newDivision, setNewDivision] = useState('');
  const [transferManagerId, setTransferManagerId] = useState('');
  const [divisions, setDivisions] = useState([]);
  
  // Fetch divisions for the division change dropdown
  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const divData = await lookupApi.getDivisions();
        setDivisions(divData.filter(d => d !== 'NO DIVISION' && d !== 'OTHERS' && d.trim() !== ''));
      } catch (err) {
        console.error('Failed to fetch divisions:', err);
      }
    };
    fetchDivisions();
  }, []);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('be_pipelineFilter', pipelineFilter);
    sessionStorage.setItem('be_productGroupFilter', productGroupFilter);
    sessionStorage.setItem('be_productNameFilter', productNameFilter);
    sessionStorage.setItem('be_list1Filter', list1Filter);
    sessionStorage.setItem('be_list2Filter', list2Filter);
    sessionStorage.setItem('be_stageFilter', stageFilter);
    sessionStorage.setItem('be_showNewDealSuspects', showNewDealSuspects);
  }, [pipelineFilter, productGroupFilter, productNameFilter, list1Filter, list2Filter, stageFilter, showNewDealSuspects]);
  
  // Check if customer needs division change
  const needsDivisionChange = useMemo(() => {
    if (!customer) return false;
    return customer.division === 'NO DIVISION' || customer.division === 'OTHERS' || !customer.division;
  }, [customer]);
  
  // Handle division update
  const handleUpdateDivision = async () => {
    if (!newDivision || !customer) return;
    try {
      await customerApi.updateDivision(customer.id, newDivision);
      setCustomer(prev => ({ ...prev, division: newDivision }));
      toast.success(`Division updated to ${newDivision}`);
      setShowDivisionDialog(false);
      setNewDivision('');
    } catch (err) {
      console.error('Division update failed:', err);
      toast.error(`Failed to update division: ${err.message}`);
    }
  };
  
  // Handle customer transfer
  const handleTransferCustomer = async () => {
    if (!transferManagerId || !customer) return;
    const manager = accountManagersList.find(m => (m.emp_id || m.id) === transferManagerId);
    if (!manager) return;
    
    try {
      await customerApi.transfer(customer.compositeId, manager.emp_id || manager.id, manager.name);
      setCustomer(prev => ({ 
        ...prev, 
        accountManager: manager.name,
        accountManagerId: manager.emp_id || manager.id
      }));
      toast.success(`Customer transferred to ${manager.name}`);
      setShowTransferDialog(false);
      setTransferManagerId('');
    } catch (err) {
      console.error('Transfer failed:', err);
      toast.error(`Failed to transfer: ${err.message}`);
    }
  };
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState([]);

  // Get unique filter options from lookups or data
  const pipelineOptions = useMemo(() => pipelines.length ? pipelines : PIPELINES.map(p => p.name), [pipelines]);
  const productGroupOptions = useMemo(() => productGroups || [], [productGroups]);
  
  // Get unique Product Name, List1, List2 options from data
  const productNameOptions = useMemo(() => {
    const names = [...new Set(budgetData.filter(r => r.productName).map(r => r.productName))];
    return names.sort();
  }, [budgetData]);
  
  const list1Options = useMemo(() => {
    const items = [...new Set(budgetData.filter(r => r.list1).map(r => r.list1))];
    return items.sort();
  }, [budgetData]);
  
  const list2Options = useMemo(() => {
    const items = [...new Set(budgetData.filter(r => r.list2).map(r => r.list2))];
    return items.sort();
  }, [budgetData]);

  const stageOptions = useMemo(() => {
    const items = [...new Set(budgetData.filter(r => r.stage && r.stage !== '-').map(r => r.stage))];
    return items.sort();
  }, [budgetData]);

  // Filter data based on toggles and filters
  const filteredData = useMemo(() => {
    let data = budgetData;
    
    // Apply filter based on toggles
    if (hideAmountRate) {
      data = data.filter(row => row.productName && row.productName.trim() !== '');
    } else if (hideQtyRate) {
      data = data.filter(row => !row.productName || row.productName.trim() === '');
    }
    
    // Apply other filters
    return data.filter(row => {
      if (pipelineFilter !== 'all' && row.pipeline !== pipelineFilter) return false;
      if (productGroupFilter !== 'all' && row.productGroup !== productGroupFilter) return false;
      if (productNameFilter !== 'all' && row.productName !== productNameFilter) return false;
      if (list1Filter !== 'all' && row.list1 !== list1Filter) return false;
      if (list2Filter !== 'all' && row.list2 !== list2Filter) return false;
      if (stageFilter !== 'all' && row.stage !== stageFilter) return false;
      // Hide NEW DEAL + SUSPECT lines unless toggle is active
      if (!showNewDealSuspects && row.pipeline === 'NEW DEAL' && (row.stage === 'SUSPECTS' || row.stage === 'SUSPECT')) return false;
      return true;
    });
  }, [budgetData, hideAmountRate, hideQtyRate, pipelineFilter, productGroupFilter, productNameFilter, list1Filter, list2Filter, stageFilter, showNewDealSuspects]);

  // Calculate totals
  const grandTotal = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  }, [filteredData]);

  // Validate data before save
  const validateData = useCallback(() => {
    const errors = [];
    budgetData.forEach((row, index) => {
      // Only validate NEW rows (user-added) that don't have a product group
      // Skip rows loaded from backend (they have an original ID or aren't marked as new)
      if (row.isEmptyRow && !row.productGroup && (row._isNewRow || row.isNewLine)) {
        const hasData = MONTHS.some(m => (row[`${m.key}Qty`] > 0 || row[`${m.key}Amount`] > 0));
        if (hasData) {
          errors.push({
            rowId: row.id,
            message: `Row ${index + 1}: Product Group is required for lines with budget data`
          });
        }
      }
    });
    setValidationErrors(errors);
    return errors.length === 0;
  }, [budgetData]);

  // Save current state to undo stack before making changes
  const pushToUndoStack = useCallback((currentData) => {
    setUndoStack(prev => {
      const newStack = [...prev, JSON.parse(JSON.stringify(currentData))];
      // Limit stack size
      if (newStack.length > MAX_HISTORY_SIZE) {
        return newStack.slice(-MAX_HISTORY_SIZE);
      }
      return newStack;
    });
    // Clear redo stack when new changes are made
    setRedoStack([]);
  }, []);

  // Undo handler
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || isLocked) return;
    
    // Save current state to redo stack
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(budgetData))]);
    
    // Pop from undo stack and restore
    const previousState = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setBudgetData(previousState);
    setHasChanges(true);
    toast.info('Undo successful');
  }, [undoStack, budgetData, isLocked]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (redoStack.length === 0 || isLocked) return;
    
    // Save current state to undo stack
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(budgetData))]);
    
    // Pop from redo stack and restore
    const nextState = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setBudgetData(nextState);
    setHasChanges(true);
    toast.info('Redo successful');
  }, [redoStack, budgetData, isLocked]);

  // Keyboard shortcuts for Undo/Redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z for Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y for Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Handle cell change with undo support
  const handleCellChange = useCallback((rowId, field, value) => {
    if (isLocked) {
      toast.error('This budget is locked and cannot be edited.');
      return;
    }

    // Save current state before change
    pushToUndoStack(budgetData);

    setBudgetData(prev => {
      const newData = [...prev];
      const rowIndex = newData.findIndex(r => r.id === rowId);
      
      if (rowIndex !== -1) {
        const row = { ...newData[rowIndex], _changed: true };
        const isProductNameRow = row.productName && row.productName.trim() !== '';
        
        if (isProductNameRow) {
          if (field.includes('Qty')) {
            row[field] = value;
            const monthKey = field.replace('Qty', '');
            row[`${monthKey}Amount`] = value * row.rate;
          }
        } else {
          if (field.includes('Amount')) {
            row[field] = value;
            const monthKey = field.replace('Amount', '');
            row[`${monthKey}Qty`] = value;
          }
        }
        
        // Recalculate totals
        row.totalQty = MONTHS.reduce((sum, m) => sum + (row[`${m.key}Qty`] || 0), 0);
        row.totalAmount = MONTHS.reduce((sum, m) => sum + (row[`${m.key}Amount`] || 0), 0);
        
        newData[rowIndex] = row;
      }
      
      return newData;
    });
    setHasChanges(true);
  }, [isLocked, budgetData, pushToUndoStack]);

  // Handle product group change for empty rows (with undo support)
  const handleProductGroupChange = useCallback((rowId, productGroup) => {
    pushToUndoStack(budgetData);
    
    setBudgetData(prev => {
      const newData = [...prev];
      const rowIndex = newData.findIndex(r => r.id === rowId);
      
      if (rowIndex !== -1) {
        const row = { ...newData[rowIndex], _changed: true };
        row.productGroup = productGroup;
        row.isEmptyRow = false; // No longer empty once product group is selected
        // Clear product name when group changes
        row.productName = '';
        row.list1 = '';
        row.list2 = '';
        row.rate = 1;
        newData[rowIndex] = row;
      }
      
      return newData;
    });
    setHasChanges(true);
  }, [budgetData, pushToUndoStack]);

  // Handle product name change - auto-fill list1, list2 from catalog; rate from price table
  const handleProductNameChange = useCallback(async (rowId, productName) => {
    pushToUndoStack(budgetData);
    
    // Find the product in catalog
    const currentRow = budgetData.find(r => r.id === rowId);
    const catalogProduct = productCatalog.find(p => 
      p.product_name === productName && p.product_group === currentRow?.productGroup
    );
    
    setBudgetData(prev => {
      const newData = [...prev];
      const rowIndex = newData.findIndex(r => r.id === rowId);
      
      if (rowIndex !== -1) {
        const row = { ...newData[rowIndex], _changed: true };
        row.productName = productName;
        row.isProductNameRow = true;
        
        if (catalogProduct) {
          const l1 = (catalogProduct.list1 && catalogProduct.list1 !== 'NA') ? catalogProduct.list1 : '';
          const l2 = (catalogProduct.list2 && catalogProduct.list2 !== 'NA') ? catalogProduct.list2 : '';
          row.list1 = l1;
          row.list2 = l2;
          row.productCode = (catalogProduct.product_code || '').trim();
          row.rate = 0; // will be fetched async
        }
        
        newData[rowIndex] = row;
      }
      
      return newData;
    });
    setHasChanges(true);
    
    // Fetch rate from price table asynchronously
    if (catalogProduct && catalogProduct.product_code && customer?.code) {
      try {
        const l1 = (catalogProduct.list1 && catalogProduct.list1 !== 'NA') ? catalogProduct.list1 : '';
        const l2 = (catalogProduct.list2 && catalogProduct.list2 !== 'NA') ? catalogProduct.list2 : '';
        const rateData = await lookupApi.getProductRate(
          catalogProduct.product_code, customer.code, l1, l2
        );
        setBudgetData(prev => prev.map(r => 
          r.id === rowId ? { ...r, rate: rateData.rate || 0, _changed: true } : r
        ));
      } catch (err) {
        console.error('Failed to fetch rate:', err);
      }
    }
  }, [productCatalog, budgetData, pushToUndoStack, customer]);

  // Get products for a specific product group from catalog
  const getProductsForGroup = useCallback((productGroup) => {
    // Return unique product names for the group
    const products = productCatalog.filter(p => p.product_group === productGroup && p.product_name);
    const seen = new Set();
    return products.filter(p => {
      if (seen.has(p.product_name)) return false;
      seen.add(p.product_name);
      return true;
    });
  }, [productCatalog]);

  // Get list1 options for a specific product (by name + group)
  const getList1ForProduct = useCallback((productGroup, productName) => {
    const items = productCatalog
      .filter(p => p.product_group === productGroup && p.product_name === productName)
      .map(p => (p.list1 && p.list1 !== 'NA' && p.list1 !== 'N/A') ? p.list1 : '')
      .filter(Boolean);
    return [...new Set(items)].sort();
  }, [productCatalog]);

  // Get list2 options for a specific product (by name + group, optionally filtered by list1)
  const getList2ForProduct = useCallback((productGroup, productName, list1Value) => {
    const items = productCatalog
      .filter(p => {
        if (p.product_group !== productGroup || p.product_name !== productName) return false;
        if (list1Value) {
          const pl1 = (p.list1 && p.list1 !== 'NA') ? p.list1 : '';
          if (pl1 !== list1Value) return false;
        }
        return true;
      })
      .map(p => (p.list2 && p.list2 !== 'NA' && p.list2 !== 'N/A') ? p.list2 : '')
      .filter(Boolean);
    return [...new Set(items)].sort();
  }, [productCatalog]);

  // Handle list1 change - fetch rate from price table
  const handleList1Change = useCallback(async (rowId, list1Value) => {
    pushToUndoStack(budgetData);
    setBudgetData(prev => prev.map(r => 
      r.id === rowId ? { ...r, list1: list1Value, _changed: true } : r
    ));
    setHasChanges(true);
    
    // Fetch rate 
    const row = budgetData.find(r => r.id === rowId);
    if (row?.productCode && customer?.code) {
      try {
        const rateData = await lookupApi.getProductRate(
          row.productCode, customer.code, list1Value, row.list2 || ''
        );
        setBudgetData(prev => prev.map(r => 
          r.id === rowId ? { ...r, rate: rateData.rate || 0, _changed: true } : r
        ));
      } catch (err) {
        console.error('Failed to fetch rate:', err);
      }
    }
  }, [budgetData, pushToUndoStack, customer]);

  // Handle list2 change - fetch rate from price table
  const handleList2Change = useCallback(async (rowId, list2Value) => {
    pushToUndoStack(budgetData);
    setBudgetData(prev => prev.map(r => 
      r.id === rowId ? { ...r, list2: list2Value, _changed: true } : r
    ));
    setHasChanges(true);
    
    // Fetch rate
    const row = budgetData.find(r => r.id === rowId);
    if (row?.productCode && customer?.code) {
      try {
        const rateData = await lookupApi.getProductRate(
          row.productCode, customer.code, row.list1 || '', list2Value
        );
        setBudgetData(prev => prev.map(r => 
          r.id === rowId ? { ...r, rate: rateData.rate || 0, _changed: true } : r
        ));
      } catch (err) {
        console.error('Failed to fetch rate:', err);
      }
    }
  }, [budgetData, pushToUndoStack, customer]);

  // Handle pipeline change for user-added lines
  const handlePipelineChange = useCallback((rowId, newPipeline) => {
    setBudgetData(prev => {
      return prev.map(row => {
        if (row.id === rowId) {
          return {
            ...row,
            pipeline: newPipeline,
            isRepeatDeal: newPipeline === 'REPEAT DEAL',
            _changed: true
          };
        }
        return row;
      });
    });
    setHasChanges(true);
  }, []);

  // Row selection handlers
  const handleRowSelect = useCallback((rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedRows.size === filteredData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredData.map(r => r.id)));
    }
  }, [filteredData, selectedRows]);

  // Get selected rows data
  const selectedRowsData = useMemo(() => {
    return budgetData.filter(r => selectedRows.has(r.id));
  }, [budgetData, selectedRows]);

  // Check if bulk actions are available
  const canChangeEmployee = useMemo(() => {
    // Change Employee is ONLY available for CROSS SELLING rows
    return selectedRowsData.length > 0 && selectedRowsData.every(r => r.pipeline === 'CROSS SELLING');
  }, [selectedRowsData]);

  const canMerge = useMemo(() => {
    // Exactly one NEW DEAL and one CROSS SELLING or UP SELLING must be selected
    const newDealRows = selectedRowsData.filter(r => r.pipeline === 'NEW DEAL');
    const targetRows = selectedRowsData.filter(r => r.pipeline === 'CROSS SELLING' || r.pipeline === 'UP SELLING');
    return newDealRows.length === 1 && targetRows.length === 1;
  }, [selectedRowsData]);

  // Bulk action: Change Employee (for CROSS SELLING lines only)
  const handleChangeEmployee = useCallback(() => {
    if (!selectedEmployee) return;
    
    const employeeName = accountManagersList.find(am => (am.emp_id || am.id) === selectedEmployee)?.name;
    
    setBudgetData(prev => {
      return prev.map(row => {
        if (selectedRows.has(row.id) && row.pipeline === 'CROSS SELLING') {
          return { ...row, assignedEmployee: employeeName, _changed: true };
        }
        return row;
      });
    });
    
    toast.success(`Assigned ${selectedRowsData.filter(r => r.pipeline === 'CROSS SELLING').length} CROSS SELLING rows to ${employeeName}`);
    setShowEmployeeDialog(false);
    setSelectedEmployee('');
    setHasChanges(true);
  }, [selectedEmployee, selectedRows, selectedRowsData, accountManagersList]);

  // Bulk action: Adjust Budget by Percentage
  const handlePercentChange = useCallback(() => {
    const percent = parseFloat(percentChange);
    if (isNaN(percent)) return;
    
    const multiplier = 1 + (percent / 100);
    
    setBudgetData(prev => {
      return prev.map(row => {
        if (selectedRows.has(row.id)) {
          const newRow = { ...row };
          MONTHS.forEach(m => {
            newRow[`${m.key}Qty`] = Math.round((newRow[`${m.key}Qty`] || 0) * multiplier);
            newRow[`${m.key}Amount`] = Math.round((newRow[`${m.key}Amount`] || 0) * multiplier);
          });
          newRow.totalQty = MONTHS.reduce((sum, m) => sum + (newRow[`${m.key}Qty`] || 0), 0);
          newRow.totalAmount = MONTHS.reduce((sum, m) => sum + (newRow[`${m.key}Amount`] || 0), 0);
          return newRow;
        }
        return row;
      });
    });
    
    toast.success(`Adjusted ${selectedRows.size} rows by ${percent > 0 ? '+' : ''}${percent}%`);
    setShowPercentDialog(false);
    setPercentChange('');
    setHasChanges(true);
  }, [percentChange, selectedRows]);

  // Bulk action: Merge Pipeline (Delete NEW DEAL, keep CROSS SELLING/UP SELLING)
  const handleMergePipeline = useCallback(() => {
    const newDealRows = selectedRowsData.filter(r => r.pipeline === 'NEW DEAL');
    const targetRows = selectedRowsData.filter(r => r.pipeline === 'CROSS SELLING' || r.pipeline === 'UP SELLING');
    
    if (newDealRows.length !== 1 || targetRows.length !== 1) return;
    
    const newDealRow = newDealRows[0];
    const targetRow = targetRows[0];
    
    setBudgetData(prev => {
      // Simply remove the NEW DEAL row, keep the target row unchanged
      return prev.filter(row => row.id !== newDealRow.id);
    });
    
    toast.success(`Merged: Removed NEW DEAL row, kept ${targetRow.pipeline} row`);
    setShowMergeDialog(false);
    setMergeTargetRow('');
    setSelectedRows(new Set());
    setHasChanges(true);
  }, [selectedRowsData]);

  // Delete selected non-REPEAT DEAL rows (with auto-save)
  const handleDeleteRows = useCallback(async () => {
    const deletableRows = selectedRowsData.filter(r => r.pipeline !== 'REPEAT DEAL');
    
    if (deletableRows.length === 0) {
      toast.error('No deletable rows selected (REPEAT DEAL cannot be deleted)');
      return;
    }
    
    // Delete from MongoDB for rows that have been saved
    for (const row of deletableRows) {
      if (row.compositeId && row._original) {
        try {
          await masterApi.deleteProduct(
            row.compositeId,
            row._original?.product_code || row.productCode || '',
            row.list1 || '',
            row.list2 || '',
            row.pipeline
          );
        } catch (err) {
          // If not found in MongoDB, that's OK - just delete locally
          if (!err.message.includes('not found')) {
            console.error('Failed to delete from MongoDB:', err);
          }
        }
      }
    }
    
    const rowIdsToDelete = new Set(deletableRows.map(r => r.id));
    
    // Update local state
    const newBudgetData = budgetData.filter(row => !rowIdsToDelete.has(row.id));
    setBudgetData(newBudgetData);
    
    toast.success(`Deleted ${deletableRows.length} row(s)`);
    setSelectedRows(new Set());
    
    // Auto-save remaining data to prevent deleted rows from reappearing
    if (newBudgetData.length > 0) {
      try {
        const savePromises = newBudgetData.map(row => {
          const productData = {
            entry_id: row.entryId,
            composite_id: row.compositeId || customer?.compositeId,
            cust_code: customer?.code,
            division: customer?.division,
            product_code: row._original?.product_code || row.productCode || `${row.pipeline}-${row.productGroup}-${Date.now()}`,
            list1: row.list1 || '',
            list2: row.list2 || '',
            product_group: row.productGroup || '',
            product_name: row.productName || null,
            pipeline: row.pipeline,
            rate: row.rate !== undefined && row.rate !== null ? row.rate : 0,
            is_repeat_deal: row.isRepeatDeal || false,
            apr_qty: row.aprQty || 0, apr_amount: row.aprAmount || 0,
            may_qty: row.mayQty || 0, may_amount: row.mayAmount || 0,
            jun_qty: row.junQty || 0, jun_amount: row.junAmount || 0,
            jul_qty: row.julQty || 0, jul_amount: row.julAmount || 0,
            aug_qty: row.augQty || 0, aug_amount: row.augAmount || 0,
            sep_qty: row.sepQty || 0, sep_amount: row.sepAmount || 0,
            oct_qty: row.octQty || 0, oct_amount: row.octAmount || 0,
            nov_qty: row.novQty || 0, nov_amount: row.novAmount || 0,
            dec_qty: row.decQty || 0, dec_amount: row.decAmount || 0,
            jan_qty: row.janQty || 0, jan_amount: row.janAmount || 0,
            feb_qty: row.febQty || 0, feb_amount: row.febAmount || 0,
            mar_qty: row.marQty || 0, mar_amount: row.marAmount || 0,
            ly_apr_qty: row.lyAprQty || 0, ly_apr_amount: row.lyAprAmount || 0,
            ly_may_qty: row.lyMayQty || 0, ly_may_amount: row.lyMayAmount || 0,
            ly_jun_qty: row.lyJunQty || 0, ly_jun_amount: row.lyJunAmount || 0,
            ly_jul_qty: row.lyJulQty || 0, ly_jul_amount: row.lyJulAmount || 0,
            ly_aug_qty: row.lyAugQty || 0, ly_aug_amount: row.lyAugAmount || 0,
            ly_sep_qty: row.lySepQty || 0, ly_sep_amount: row.lySepAmount || 0,
            ly_oct_qty: row.lyOctQty || 0, ly_oct_amount: row.lyOctAmount || 0,
            ly_nov_qty: row.lyNovQty || 0, ly_nov_amount: row.lyNovAmount || 0,
            ly_dec_qty: row.lyDecQty || 0, ly_dec_amount: row.lyDecAmount || 0,
            ly_jan_qty: row.lyJanQty || 0, ly_jan_amount: row.lyJanAmount || 0,
            ly_feb_qty: row.lyFebQty || 0, ly_feb_amount: row.lyFebAmount || 0,
            ly_mar_qty: row.lyMarQty || 0, ly_mar_amount: row.lyMarAmount || 0,
          };
          return masterApi.updateProduct(productData);
        });
        await Promise.all(savePromises);
        toast.success('Auto-saved remaining data');
        setHasChanges(false);
      } catch (err) {
        console.error('Auto-save failed:', err);
        toast.error('Auto-save failed');
      }
    }
  }, [selectedRowsData, budgetData, customer]);

  // Delete a single non-REPEAT DEAL row (called from row action button)
  const handleDeleteSingleRow = useCallback(async (rowId) => {
    const row = budgetData.find(r => r.id === rowId);
    if (!row || row.pipeline === 'REPEAT DEAL') {
      toast.error('REPEAT DEAL rows cannot be deleted');
      return;
    }
    
    // If the row has been saved to MongoDB (has composite_id), delete from MongoDB too
    if (row.compositeId && row._original) {
      try {
        await masterApi.deleteProduct(
          row.compositeId,
          row._original?.product_code || row.productCode || '',
          row.list1 || '',
          row.list2 || '',
          row.pipeline
        );
      } catch (err) {
        // If not found in MongoDB, that's OK - just delete locally
        if (!err.message.includes('not found')) {
          console.error('Failed to delete from MongoDB:', err);
        }
      }
    }
    
    // Update local state
    const newBudgetData = budgetData.filter(r => r.id !== rowId);
    setBudgetData(newBudgetData);
    toast.success(`Deleted ${row.pipeline} row`);
    
    // Auto-save remaining data
    if (newBudgetData.length > 0) {
      try {
        const savePromises = newBudgetData.map(r => {
          const productData = {
            entry_id: r.entryId,
            composite_id: r.compositeId || customer?.compositeId,
            cust_code: customer?.code,
            division: customer?.division,
            product_code: r._original?.product_code || r.productCode || `${r.pipeline}-${r.productGroup}-${Date.now()}`,
            list1: r.list1 || '',
            list2: r.list2 || '',
            product_group: r.productGroup || '',
            product_name: r.productName || null,
            pipeline: r.pipeline,
            rate: r.rate !== undefined && r.rate !== null ? r.rate : 0,
            is_repeat_deal: r.isRepeatDeal || false,
            apr_qty: r.aprQty || 0, apr_amount: r.aprAmount || 0,
            may_qty: r.mayQty || 0, may_amount: r.mayAmount || 0,
            jun_qty: r.junQty || 0, jun_amount: r.junAmount || 0,
            jul_qty: r.julQty || 0, jul_amount: r.julAmount || 0,
            aug_qty: r.augQty || 0, aug_amount: r.augAmount || 0,
            sep_qty: r.sepQty || 0, sep_amount: r.sepAmount || 0,
            oct_qty: r.octQty || 0, oct_amount: r.octAmount || 0,
            nov_qty: r.novQty || 0, nov_amount: r.novAmount || 0,
            dec_qty: r.decQty || 0, dec_amount: r.decAmount || 0,
            jan_qty: r.janQty || 0, jan_amount: r.janAmount || 0,
            feb_qty: r.febQty || 0, feb_amount: r.febAmount || 0,
            mar_qty: r.marQty || 0, mar_amount: r.marAmount || 0,
            ly_apr_qty: r.lyAprQty || 0, ly_apr_amount: r.lyAprAmount || 0,
            ly_may_qty: r.lyMayQty || 0, ly_may_amount: r.lyMayAmount || 0,
            ly_jun_qty: r.lyJunQty || 0, ly_jun_amount: r.lyJunAmount || 0,
            ly_jul_qty: r.lyJulQty || 0, ly_jul_amount: r.lyJulAmount || 0,
            ly_aug_qty: r.lyAugQty || 0, ly_aug_amount: r.lyAugAmount || 0,
            ly_sep_qty: r.lySepQty || 0, ly_sep_amount: r.lySepAmount || 0,
            ly_oct_qty: r.lyOctQty || 0, ly_oct_amount: r.lyOctAmount || 0,
            ly_nov_qty: r.lyNovQty || 0, ly_nov_amount: r.lyNovAmount || 0,
            ly_dec_qty: r.lyDecQty || 0, ly_dec_amount: r.lyDecAmount || 0,
            ly_jan_qty: r.lyJanQty || 0, ly_jan_amount: r.lyJanAmount || 0,
            ly_feb_qty: r.lyFebQty || 0, ly_feb_amount: r.lyFebAmount || 0,
            ly_mar_qty: r.lyMarQty || 0, ly_mar_amount: r.lyMarAmount || 0,
          };
          return masterApi.updateProduct(productData);
        });
        await Promise.all(savePromises);
        setHasChanges(false);
      } catch (err) {
        console.error('Auto-save failed:', err);
      }
    }
  }, [budgetData, customer]);

  // Add a new empty line for user to fill in
  const handleAddNewLine = useCallback((pipelineType = 'NEW DEAL') => {
    const newId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    const newRow = {
      id: newId,
      entryId: null,
      compositeId: customer?.compositeId,
      pipeline: pipelineType,
      productGroup: '',
      productName: '',
      productCode: '',
      list1: '',
      list2: '',
      rate: 0,
      isRepeatDeal: pipelineType === 'REPEAT DEAL',
      isEmptyRow: true,
      isNewLine: true,  // Flag to identify user-added lines
      _changed: true,
      _original: null,
      color: 'default',
      // Initialize all month fields to 0
      ...MONTHS.reduce((acc, m) => ({
        ...acc,
        [`${m.key}Qty`]: 0,
        [`${m.key}Amount`]: 0,
        [`ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Qty`]: 0,
        [`ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Amount`]: 0,
      }), {}),
    };
    
    // Add at the beginning of the list (after existing rows of same pipeline)
    setBudgetData(prev => {
      // Find the index to insert (after last row of same pipeline, or at beginning)
      const sameTypePipelineIndices = prev.map((r, i) => r.pipeline === pipelineType ? i : -1).filter(i => i !== -1);
      const insertIndex = sameTypePipelineIndices.length > 0 ? Math.max(...sameTypePipelineIndices) + 1 : 0;
      
      const newData = [...prev];
      newData.splice(insertIndex, 0, newRow);
      return newData;
    });
    
    toast.success(`Added new ${pipelineType} line`);
    setHasChanges(true);
  }, [customer?.compositeId]);

  // State for "Add Line" dialog
  const [showAddLineDialog, setShowAddLineDialog] = useState(false);
  const [newLinePipeline, setNewLinePipeline] = useState('NEW DEAL');

  // Export to Excel (CSV) - Export ALL data
  const handleExport = useCallback(() => {
    const columns = [
      { key: 'pipeline', label: 'Pipeline' },
      { key: 'stage', label: 'Stage' },
      { key: 'productGroup', label: 'Product Group' },
      { key: 'productName', label: 'Product Name' },
      { key: 'list1', label: 'List 1' },
      { key: 'list2', label: 'List 2' },
      { key: 'rate', label: 'Rate' },
      // QTY columns first (LY + TY per month)
      ...MONTHS.flatMap(m => [
        { key: `ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Qty`, label: `LY ${m.label} Qty` },
        { key: `${m.key}Qty`, label: `TY ${m.label} Qty` },
      ]),
      { key: 'lyTotalQty', label: 'LY Total Qty' },
      { key: 'totalQty', label: 'TY Total Qty' },
      // AMT columns next (LY + TY per month)
      ...MONTHS.flatMap(m => [
        { key: `ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Amount`, label: `LY ${m.label} Amt` },
        { key: `${m.key}Amount`, label: `TY ${m.label} Amt` },
      ]),
      { key: 'lyTotalAmount', label: 'LY Total Amount' },
      { key: 'totalAmount', label: 'TY Total Amount' },
      // Color coding indicator
      { key: 'color', label: 'Status Color' },
    ];
    
    const csv = convertToCSV(budgetData, columns);
    downloadCSV(csv, `${customer?.name}_Complete_Budget.csv`);
    toast.success('Complete budget exported with LY & TY data');
  }, [budgetData, customer]);

  // Save handlers
  const handleSave = async () => {
    if (isLocked) return;
    
    // Validate before saving
    if (!validateData()) {
      toast.error('Please select Product Group for all lines with budget data before saving.');
      return;
    }
    
    setIsSaving(true);
    try {
      // Save ALL products for this customer to MongoDB (not just changed ones)
      // This makes MongoDB the complete source of truth for this customer's budget
      // Include BOTH TY (editable) and LY (reference) data
      // Filter out placeholder rows that have no product group (empty Add Line rows)
      const rowsToSave = budgetData.filter(row => row.productGroup && row.productGroup.trim() !== '');
      const savePromises = rowsToSave.map(row => {
          const productData = {
            entry_id: row.entryId,  // Include entry_id for reference
            composite_id: row.compositeId || customer?.compositeId,  // Include composite_id for MongoDB grouping
            cust_code: customer?.code,
            division: customer?.division,  // Include division for stats aggregation
            product_code: (row.productCode || row._original?.product_code || `${row.pipeline}-${row.productGroup}-${Date.now()}`).trim(),
            list1: (row.list1 || '').trim(),
            list2: (row.list2 || '').trim(),
            product_group: row.productGroup,
            product_name: row.productName || null,
            pipeline: row.pipeline,
            rate: row.rate !== undefined && row.rate !== null ? row.rate : 0,
            is_repeat_deal: row.isRepeatDeal || false,
            // THIS YEAR (TY) data - editable values
            apr_qty: row.aprQty || 0, apr_amount: row.aprAmount || 0,
            may_qty: row.mayQty || 0, may_amount: row.mayAmount || 0,
            jun_qty: row.junQty || 0, jun_amount: row.junAmount || 0,
            jul_qty: row.julQty || 0, jul_amount: row.julAmount || 0,
            aug_qty: row.augQty || 0, aug_amount: row.augAmount || 0,
            sep_qty: row.sepQty || 0, sep_amount: row.sepAmount || 0,
            oct_qty: row.octQty || 0, oct_amount: row.octAmount || 0,
            nov_qty: row.novQty || 0, nov_amount: row.novAmount || 0,
            dec_qty: row.decQty || 0, dec_amount: row.decAmount || 0,
            jan_qty: row.janQty || 0, jan_amount: row.janAmount || 0,
            feb_qty: row.febQty || 0, feb_amount: row.febAmount || 0,
            mar_qty: row.marQty || 0, mar_amount: row.marAmount || 0,
            // LAST YEAR (LY) data - reference values for growth calculation
            ly_apr_qty: row.lyAprQty || 0, ly_apr_amount: row.lyAprAmount || 0,
            ly_may_qty: row.lyMayQty || 0, ly_may_amount: row.lyMayAmount || 0,
            ly_jun_qty: row.lyJunQty || 0, ly_jun_amount: row.lyJunAmount || 0,
            ly_jul_qty: row.lyJulQty || 0, ly_jul_amount: row.lyJulAmount || 0,
            ly_aug_qty: row.lyAugQty || 0, ly_aug_amount: row.lyAugAmount || 0,
            ly_sep_qty: row.lySepQty || 0, ly_sep_amount: row.lySepAmount || 0,
            ly_oct_qty: row.lyOctQty || 0, ly_oct_amount: row.lyOctAmount || 0,
            ly_nov_qty: row.lyNovQty || 0, ly_nov_amount: row.lyNovAmount || 0,
            ly_dec_qty: row.lyDecQty || 0, ly_dec_amount: row.lyDecAmount || 0,
            ly_jan_qty: row.lyJanQty || 0, ly_jan_amount: row.lyJanAmount || 0,
            ly_feb_qty: row.lyFebQty || 0, ly_feb_amount: row.lyFebAmount || 0,
            ly_mar_qty: row.lyMarQty || 0, ly_mar_amount: row.lyMarAmount || 0,
          };
          return masterApi.updateProduct(productData);
        });
      
      if (savePromises.length > 0) {
        await Promise.all(savePromises);
        toast.success(`Saved ${savePromises.length} product lines`);
      } else {
        toast.info('No product lines to save (add a Product Group first)');
      }
      
      setHasChanges(false);
      // Mark all rows as saved
      setBudgetData(prev => prev.map(row => ({ ...row, _changed: false })));
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Recalculate all TY amounts: amount = qty × rate for each month
  const handleRecalculate = useCallback(() => {
    if (isLocked) return;
    pushToUndoStack(budgetData);
    
    setBudgetData(prev => {
      const newData = prev.map(row => {
        const isProductNameRow = row.productName && row.productName.trim() !== '';
        if (!isProductNameRow) return row; // Skip empty/non-product rows
        
        const rate = parseFloat(row.rate) || 0;
        const updatedRow = { ...row, _changed: true };
        
        MONTHS.forEach(m => {
          const qty = parseFloat(updatedRow[`${m.key}Qty`]) || 0;
          updatedRow[`${m.key}Amount`] = qty * rate;
        });
        
        updatedRow.totalQty = MONTHS.reduce((sum, m) => sum + (parseFloat(updatedRow[`${m.key}Qty`]) || 0), 0);
        updatedRow.totalAmount = MONTHS.reduce((sum, m) => sum + (parseFloat(updatedRow[`${m.key}Amount`]) || 0), 0);
        
        return updatedRow;
      });
      return newData;
    });
    
    setHasChanges(true);
    toast.success('TY amounts recalculated (Qty × Rate)');
  }, [isLocked, budgetData, pushToUndoStack]);


  const handleSaveAndExit = async () => {
    if (isLocked) return;
    
    if (!validateData()) {
      toast.error('Please select Product Group for all lines with budget data before saving.');
      return;
    }
    
    await handleSave();
    // Close tab if opened as new tab from CustomerList, otherwise navigate back
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  const handleExit = () => {
    if (window.opener) {
      window.close();
    } else {
      navigate('/');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setPipelineFilter('all');
    setProductGroupFilter('all');
    setProductNameFilter('all');
    setList1Filter('all');
    setList2Filter('all');
    setStageFilter('all');
    setShowNewDealSuspects(false);
    setHideAmountRate(false);
    setHideQtyRate(false);
  };

  // Handle toggle changes - ensure mutual exclusivity
  const handleHideAmountRateChange = (checked) => {
    setHideAmountRate(checked);
    if (checked) setHideQtyRate(false);
  };

  const handleHideQtyRateChange = (checked) => {
    setHideQtyRate(checked);
    if (checked) setHideAmountRate(false);
  };

  // Get merge target options (CROSS SELLING and UP SELLING rows)
  const mergeTargetOptions = useMemo(() => {
    return budgetData.filter(r => 
      (r.pipeline === 'CROSS SELLING' || r.pipeline === 'UP SELLING') && 
      !selectedRows.has(r.id)
    );
  }, [budgetData, selectedRows]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] pb-20" data-testid="budget-entry-skeleton">
        {/* Skeleton Header */}
        <div className="sticky top-0 z-30 bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse" />
              <div>
                <div className="h-5 w-48 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-32 bg-gray-100 rounded animate-pulse mt-1.5" />
              </div>
            </div>
            <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
        {/* Skeleton Filters */}
        <div className="sticky top-[57px] z-20 bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex gap-2">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="h-8 w-28 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
        {/* Skeleton Table */}
        <div className="px-4 mt-2">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Header row */}
            <div className="flex bg-gray-50 border-b border-gray-200 py-2 px-2">
              {[30,100,80,140,140,80,80,70,70,70,70,70,70,70,70,70,70,70,70,70].map((w, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded animate-pulse mx-1" style={{width: `${w}px`, flexShrink: 0}} />
              ))}
            </div>
            {/* Data rows */}
            {Array.from({length: 15}).map((_, ri) => (
              <div key={ri} className={`flex border-b border-gray-100 py-2.5 px-2 ${ri % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                {[30,100,80,140,140,80,80,70,70,70,70,70,70,70,70,70,70,70,70,70].map((w, ci) => (
                  <div key={ci} className="h-3.5 bg-gray-100 rounded animate-pulse mx-1" style={{width: `${w}px`, flexShrink: 0, animationDelay: `${(ri * 20 + ci * 10) % 300}ms`}} />
                ))}
              </div>
            ))}
          </div>
        </div>
        {/* Skeleton Footer */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <div className="h-8 w-8 bg-gray-100 rounded animate-pulse" />
              <div className="h-8 w-8 bg-gray-100 rounded animate-pulse" />
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-24 bg-gray-100 rounded animate-pulse" />
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Budget</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={handleExit}>Back to Customer List</Button>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-gray-900">Customer not found</h2>
        <Button onClick={handleExit} className="mt-4">
          Back to Customer List
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-20" data-testid="budget-entry-page">
      {/* Employee Assignment Dialog */}
      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Assigned Employee</DialogTitle>
            <DialogDescription>
              Select an employee to assign to {selectedRowsData.filter(r => r.pipeline === 'CROSS SELLING').length} selected CROSS SELLING rows.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger>
                <SelectValue placeholder="Select Employee" />
              </SelectTrigger>
              <SelectContent>
                {accountManagersList.map(am => (
                  <SelectItem key={am.emp_id || am.id} value={am.emp_id || am.id}>{am.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmployeeDialog(false)}>Cancel</Button>
            <Button onClick={handleChangeEmployee} disabled={!selectedEmployee}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Percentage Change Dialog */}
      <Dialog open={showPercentDialog} onOpenChange={setShowPercentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Budget by Percentage</DialogTitle>
            <DialogDescription>
              Enter a percentage to increase (+) or decrease (-) the budget for {selectedRows.size} selected rows.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="e.g., 10 or -5"
                value={percentChange}
                onChange={(e) => setPercentChange(e.target.value)}
                className="flex-1"
              />
              <span className="text-lg font-medium">%</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPercentDialog(false)}>Cancel</Button>
            <Button onClick={handlePercentChange} disabled={!percentChange}>Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Pipeline Dialog */}
      <MergePipelineDialog
        open={showMergeDialog}
        onOpenChange={setShowMergeDialog}
        selectedRowsData={selectedRowsData}
        onMerge={handleMergePipeline}
      />

      <DivisionChangeDialog
        open={showDivisionDialog}
        onOpenChange={setShowDivisionDialog}
        customer={customer}
        divisions={divisions}
        newDivision={newDivision}
        setNewDivision={setNewDivision}
        onUpdate={handleUpdateDivision}
      />

      <TransferDialog
        open={showTransferDialog}
        onOpenChange={setShowTransferDialog}
        customer={customer}
        managers={accountManagersList}
        transferManagerId={transferManagerId}
        setTransferManagerId={setTransferManagerId}
        onTransfer={handleTransferCustomer}
      />

      {/* Customer Info Header - Fixed below main header */}
      <div className="bg-white border-b border-gray-200 sticky top-[57px] z-30">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Back + Customer Info */}
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExit}
                className="text-gray-600 hover:text-gray-900 -ml-2"
                data-testid="back-button"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isLocked ? 'bg-amber-100' : 'bg-zinc-900'}`}>
                  {isLocked ? (
                    <Lock className="h-5 w-5 text-amber-600" />
                  ) : (
                    <span className="text-sm font-bold text-white">
                      {customer.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </span>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="font-heading text-lg font-bold text-gray-900" data-testid="customer-name">
                      {customer.name}
                    </h1>
                    {isLocked && (
                      <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">
                        Locked
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    {/* Division - Clickable if needs change and not locked */}
                    {needsDivisionChange && !isLocked ? (
                      <button 
                        onClick={() => setShowDivisionDialog(true)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors"
                      >
                        <FolderEdit className="h-3 w-3" />
                        {customer.division || 'No Division'}
                      </button>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {customer.division}
                      </span>
                    )}
                    <span>•</span>
                    {/* Account Manager - Clickable for transfer if not locked */}
                    {!isLocked ? (
                      <button 
                        onClick={() => setShowTransferDialog(true)}
                        className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-blue-50 text-blue-600 transition-colors"
                      >
                        <UserPlus className="h-3 w-3" />
                        {customer.accountManager}
                      </button>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {customer.accountManager}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Diff + Total Budget + Export */}
            <div className="flex items-center gap-4">
              {customer.lastYearTotal > 0 && (
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                  grandTotal >= customer.lastYearTotal 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-red-50 text-red-700'
                }`}>
                  {grandTotal >= customer.lastYearTotal ? (
                    <TrendingUp className="h-3.5 w-3.5" />
                  ) : (
                    <TrendingDown className="h-3.5 w-3.5" />
                  )}
                  <span>
                    {grandTotal >= customer.lastYearTotal ? '+' : ''}
                    {(((grandTotal - customer.lastYearTotal) / customer.lastYearTotal) * 100).toFixed(1)}%
                  </span>
                  <span className="text-[10px] opacity-70">vs LY</span>
                </div>
              )}
              
              <div className="text-right">
                <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Budget</p>
                <p className="font-mono text-xl font-bold text-gray-900" data-testid="total-budget">
                  {formatCrores(grandTotal)}
                </p>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="border-gray-200"
                data-testid="export-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Locked Banner */}
      {isLocked && (
        <div className={`border-b px-6 py-2 ${lockedByUser ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            <Lock className={`h-4 w-4 ${lockedByUser ? 'text-blue-600' : 'text-amber-600'}`} />
            <p className={`text-sm ${lockedByUser ? 'text-blue-800' : 'text-amber-800'}`}>
              {lockedByUser
                ? `Currently being edited by ${lockedByUser.name || lockedByUser.emp_id}. You can view but cannot edit.`
                : 'This budget is approved and locked. You can view but cannot edit.'}
            </p>
            <Badge className={`ml-auto border-0 text-xs ${lockedByUser ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>
              <Eye className="h-3 w-3 mr-1" />
              View Only
            </Badge>
          </div>
        </div>
      )}

      {/* Validation Errors Banner */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <p className="text-sm text-red-800">
              {validationErrors.length} row(s) require Product Group selection before saving.
            </p>
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-auto text-red-600 hover:text-red-700"
              onClick={() => setValidationErrors([])}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar - Shows when rows are selected */}
      {selectedRows.size > 0 && !isLocked && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-2 sticky top-[114px] z-20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                {selectedRows.size} row(s) selected
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-blue-600 hover:text-blue-700 h-7"
                onClick={() => setSelectedRows(new Set())}
              >
                Clear
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {/* Change Employee - For CROSS SELLING lines only */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                disabled={!canChangeEmployee}
                onClick={() => setShowEmployeeDialog(true)}
                title={canChangeEmployee ? 'Change assigned employee for CROSS SELLING lines' : 'Only available for CROSS SELLING lines'}
              >
                <Users className="h-3.5 w-3.5 mr-1" />
                Change Employee
              </Button>

              {/* Adjust Percentage */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                onClick={() => setShowPercentDialog(true)}
              >
                <Percent className="h-3.5 w-3.5 mr-1" />
                Adjust %
              </Button>

              {/* Merge Pipeline - One NEW DEAL + One CROSS SELLING or UP SELLING */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-100"
                disabled={!canMerge}
                onClick={() => setShowMergeDialog(true)}
                title={canMerge ? 'Delete NEW DEAL and keep CROSS SELLING/UP SELLING' : 'Select exactly 1 NEW DEAL and 1 CROSS SELLING/UP SELLING row'}
              >
                <Merge className="h-3.5 w-3.5 mr-1" />
                Merge Pipeline
              </Button>

              {/* Delete non-REPEAT DEAL rows */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-100"
                disabled={!selectedRowsData.some(r => r.pipeline !== 'REPEAT DEAL')}
                onClick={handleDeleteRows}
                title={selectedRowsData.some(r => r.pipeline !== 'REPEAT DEAL') ? 'Delete selected rows (auto-saves)' : 'Select non-REPEAT DEAL rows to delete'}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete Rows
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 py-4">
        {/* Filters and View Controls */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Filter className="h-4 w-4 text-gray-400" />
              
              <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                <SelectTrigger className="w-32 h-8 text-xs bg-gray-50 border-gray-200" data-testid="be-filter-pipeline">
                  <SelectValue placeholder="Pipeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Pipelines</SelectItem>
                  {pipelineOptions.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-28 h-8 text-xs bg-gray-50 border-gray-200" data-testid="be-filter-stage">
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {stageOptions.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={productGroupFilter} onValueChange={setProductGroupFilter}>
                <SelectTrigger className="w-40 h-8 text-xs bg-gray-50 border-gray-200">
                  <SelectValue placeholder="Product Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Product Groups</SelectItem>
                  {productGroupOptions.map(pg => (
                    <SelectItem key={pg} value={pg}>{pg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Product Name, List1, List2 filters - only show when columns are visible */}
              {!hideQtyRate && (
                <>
                  <Select value={productNameFilter} onValueChange={setProductNameFilter}>
                    <SelectTrigger className="w-36 h-8 text-xs bg-gray-50 border-gray-200">
                      <SelectValue placeholder="Product Name" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      {productNameOptions.map(pn => (
                        <SelectItem key={pn} value={pn}>{pn}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={list1Filter} onValueChange={setList1Filter}>
                    <SelectTrigger className="w-28 h-8 text-xs bg-gray-50 border-gray-200">
                      <SelectValue placeholder="List 1" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All List 1</SelectItem>
                      {list1Options.map(l => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={list2Filter} onValueChange={setList2Filter}>
                    <SelectTrigger className="w-28 h-8 text-xs bg-gray-50 border-gray-200">
                      <SelectValue placeholder="List 2" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All List 2</SelectItem>
                      {list2Options.map(l => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8 text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </Button>
            </div>

            {/* View toggles */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Switch
                  checked={showNewDealSuspects}
                  onCheckedChange={setShowNewDealSuspects}
                  id="show-suspects"
                  className="h-4 w-8"
                  data-testid="toggle-show-suspects-be"
                />
                <label htmlFor="show-suspects" className="text-xs font-medium text-yellow-700 flex items-center gap-1 cursor-pointer">
                  Show Suspects
                </label>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <Switch
                  checked={hideLastYear}
                  onCheckedChange={setHideLastYear}
                  id="hide-last-year"
                  className="h-4 w-8"
                />
                <label htmlFor="hide-last-year" className="text-xs font-medium text-gray-600 flex items-center gap-1 cursor-pointer">
                  <EyeOff className="h-3.5 w-3.5" />
                  Hide Last Year
                </label>
              </div>
              <div className="h-4 w-px bg-gray-300" />
              <div className="flex items-center gap-2">
                <Switch
                  checked={hideAmountRate}
                  onCheckedChange={handleHideAmountRateChange}
                  id="hide-amount-rate"
                  className="h-4 w-8"
                />
                <label htmlFor="hide-amount-rate" className="text-xs font-medium text-gray-600 flex items-center gap-1 cursor-pointer">
                  <Package className="h-3.5 w-3.5" />
                  Hide Amount & Rate
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={hideQtyRate}
                  onCheckedChange={handleHideQtyRateChange}
                  id="hide-qty-rate"
                  className="h-4 w-8"
                />
                <label htmlFor="hide-qty-rate" className="text-xs font-medium text-gray-600 flex items-center gap-1 cursor-pointer">
                  <Boxes className="h-3.5 w-3.5" />
                  Hide Qty & Rate
                </label>
              </div>
              <span className="text-xs text-gray-500 ml-2">
                {filteredData.length} records
              </span>
              {/* Row Color Legend */}
              <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-200">
                <span className="text-[10px] text-gray-400 uppercase tracking-wider">Legend:</span>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded border-l-2 border-l-emerald-500 bg-emerald-50" />
                  <span className="text-[10px] text-gray-500">Repeat Deal (Invoice)</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-green-50 border border-green-200" />
                  <span className="text-[10px] text-gray-500">Active</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-yellow-50 border border-yellow-200" />
                  <span className="text-[10px] text-gray-500">New/Dormant</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Merged Budget Table */}
        <MergedBudgetTable
          data={filteredData}
          allData={budgetData}
          onCellChange={handleCellChange}
          onProductGroupChange={handleProductGroupChange}
          onProductNameChange={handleProductNameChange}
          onPipelineChange={handlePipelineChange}
          onList1Change={handleList1Change}
          onList2Change={handleList2Change}
          productGroupOptions={productGroups}
          getProductsForGroup={getProductsForGroup}
          getList1ForProduct={getList1ForProduct}
          getList2ForProduct={getList2ForProduct}
          isReadOnly={isLocked}
          hideAmountRate={hideAmountRate}
          hideQtyRate={hideQtyRate}
          hideLastYear={hideLastYear}
          selectedRows={selectedRows}
          onRowSelect={handleRowSelect}
          onSelectAll={handleSelectAll}
          validationErrors={validationErrors}
          hasSelectedRows={selectedRows.size > 0}
          hasValidationErrors={validationErrors.length > 0}
        />
      </div>

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-6 py-3 z-40" data-testid="action-bar">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {isLocked ? (
              <span className="text-sm text-amber-600 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                Budget is locked - View only mode
              </span>
            ) : hasChanges ? (
              <span className="text-sm text-amber-600 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                Unsaved changes
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            {isLocked ? (
              <Button
                variant="outline"
                onClick={handleExit}
                className="border-gray-200"
                data-testid="exit-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Exit
              </Button>
            ) : (
              <>
                {/* Undo/Redo Buttons */}
                <div className="flex items-center border-r border-gray-200 pr-3 mr-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleUndo}
                    disabled={undoStack.length === 0 || isLocked}
                    className="h-8 w-8 p-0"
                    title="Undo (Ctrl+Z)"
                    data-testid="undo-button"
                  >
                    <Undo2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRedo}
                    disabled={redoStack.length === 0 || isLocked}
                    className="h-8 w-8 p-0"
                    title="Redo (Ctrl+Y)"
                    data-testid="redo-button"
                  >
                    <Redo2 className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Recalculate Button */}
                <div className="flex items-center border-r border-gray-200 pr-3 mr-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRecalculate}
                    className="h-8 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
                    title="Recalculate all TY amounts (Qty × Rate)"
                    data-testid="recalculate-button"
                  >
                    <Calculator className="h-3.5 w-3.5 mr-1" />
                    Recalculate
                  </Button>
                </div>

                {/* Add Line Button */}
                <div className="flex items-center border-r border-gray-200 pr-3 mr-3">
                  <Select value={newLinePipeline} onValueChange={setNewLinePipeline}>
                    <SelectTrigger className="h-8 w-[130px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEW DEAL">NEW DEAL</SelectItem>
                      <SelectItem value="CROSS SELLING">CROSS SELLING</SelectItem>
                      <SelectItem value="UP SELLING">UP SELLING</SelectItem>
                      <SelectItem value="WIN BACK">WIN BACK</SelectItem>
                      <SelectItem value="INDENTING DEAL">INDENTING DEAL</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddNewLine(newLinePipeline)}
                    className="h-8 ml-1 text-xs border-green-300 text-green-700 hover:bg-green-50"
                    title="Add a new budget line"
                    data-testid="add-line-button"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Line
                  </Button>
                </div>
                
                <Button
                  variant="outline"
                  onClick={handleSaveAndExit}
                  disabled={isSaving}
                  className="border-gray-200"
                  data-testid="save-exit-button"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
                  Save & Exit
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-zinc-900 hover:bg-zinc-800 text-white"
                  data-testid="save-button"
                >
                  {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Merged Budget Table Component
const MergedBudgetTable = ({ 
  data, 
  allData,
  onCellChange, 
  onProductGroupChange,
  onProductNameChange,
  onPipelineChange,
  onList1Change,
  onList2Change,
  productGroupOptions,
  getProductsForGroup,
  getList1ForProduct,
  getList2ForProduct,
  isReadOnly, 
  hideAmountRate, 
  hideQtyRate,
  hideLastYear,
  selectedRows,
  onRowSelect,
  onSelectAll,
  validationErrors,
  hasSelectedRows,
  hasValidationErrors
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const inputRefs = useRef({});
  const tableContainerRef = useRef(null);

  // Progressive loading: render rows in chunks
  const INITIAL_ROWS = 30;
  const LOAD_MORE_COUNT = 20;
  const [visibleCount, setVisibleCount] = useState(INITIAL_ROWS);

  // Reset visible count when data changes (e.g., filters)
  useEffect(() => {
    setVisibleCount(INITIAL_ROWS);
  }, [data.length]);

  // Load more rows on scroll near bottom
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 200 && visibleCount < data.length) {
        setVisibleCount(prev => Math.min(prev + LOAD_MORE_COUNT, data.length));
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [visibleCount, data.length]);

  const visibleData = data.slice(0, visibleCount);

  // Calculate dynamic table height based on active bars
  // Base: 57px header + 60px customer info + 56px filters + 60px bottom action bar + 16px padding = ~250px
  // Add 44px for bulk action bar if rows are selected
  // Add 36px for validation error bar if errors exist
  const getTableMaxHeight = () => {
    let extraHeight = 0;
    if (hasSelectedRows && !isReadOnly) extraHeight += 44; // Bulk action bar height
    if (hasValidationErrors) extraHeight += 36; // Validation error bar height
    return `calc(100vh - ${320 + extraHeight}px)`;
  };

  const handleCellClick = (rowId, field, isEditable) => {
    if (isReadOnly || !isEditable) return;
    const cellId = `${rowId}-${field}`;
    const row = data.find(r => r.id === rowId);
    if (row) {
      setEditingCell(cellId);
      setEditValue(String(row[field] || ''));
      setTimeout(() => {
        const input = inputRefs.current[cellId];
        if (input) {
          input.focus();
          input.select();
        }
      }, 0);
    }
  };

  const handleBlur = (rowId, field) => {
    if (editingCell) {
      const numValue = parseFloat(editValue) || 0;
      onCellChange(rowId, field, numValue);
      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleKeyDown = (e, rowId, field, rowIndex) => {
    const row = visibleData[rowIndex];
    const isProductNameRow = row.productName && row.productName.trim() !== '';
    
    const getEditableFields = (isProductRow) => {
      const fields = [];
      MONTHS.forEach(m => {
        if (isProductRow) {
          fields.push(`${m.key}Qty`);
        } else {
          fields.push(`${m.key}Amount`);
        }
      });
      return fields;
    };
    
    const editableFields = getEditableFields(isProductNameRow);
    const currentFieldIndex = editableFields.indexOf(field);
    
    if (e.key === 'Tab') {
      e.preventDefault();
      handleBlur(rowId, field);
      
      if (e.shiftKey) {
        if (currentFieldIndex > 0) {
          const prevField = editableFields[currentFieldIndex - 1];
          setTimeout(() => handleCellClick(rowId, prevField, true), 0);
        } else if (rowIndex > 0) {
          const prevRow = visibleData[rowIndex - 1];
          const prevRowIsProductName = prevRow.productName && prevRow.productName.trim() !== '';
          const prevRowFields = getEditableFields(prevRowIsProductName);
          const lastField = prevRowFields[prevRowFields.length - 1];
          setTimeout(() => handleCellClick(prevRow.id, lastField, true), 0);
        }
      } else {
        if (currentFieldIndex < editableFields.length - 1) {
          const nextField = editableFields[currentFieldIndex + 1];
          setTimeout(() => handleCellClick(rowId, nextField, true), 0);
        } else if (rowIndex < visibleData.length - 1) {
          const nextRow = visibleData[rowIndex + 1];
          const nextRowIsProductName = nextRow.productName && nextRow.productName.trim() !== '';
          const nextRowFields = getEditableFields(nextRowIsProductName);
          const firstField = nextRowFields[0];
          setTimeout(() => handleCellClick(nextRow.id, firstField, true), 0);
        }
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur(rowId, field);
      if (rowIndex < visibleData.length - 1) {
        const nextRow = visibleData[rowIndex + 1];
        setTimeout(() => {
          const nextIsProductNameRow = nextRow.productName && nextRow.productName.trim() !== '';
          const isEditable = nextIsProductNameRow ? field.includes('Qty') : field.includes('Amount');
          if (isEditable) {
            handleCellClick(nextRow.id, field, true);
          }
        }, 0);
      }
    } else if (e.key === 'ArrowRight' && currentFieldIndex < editableFields.length - 1) {
      e.preventDefault();
      handleBlur(rowId, field);
      const nextField = editableFields[currentFieldIndex + 1];
      setTimeout(() => handleCellClick(rowId, nextField, true), 0);
    } else if (e.key === 'ArrowLeft' && currentFieldIndex > 0) {
      e.preventDefault();
      handleBlur(rowId, field);
      const prevField = editableFields[currentFieldIndex - 1];
      setTimeout(() => handleCellClick(rowId, prevField, true), 0);
    } else if (e.key === 'ArrowDown' && rowIndex < data.length - 1) {
      e.preventDefault();
      handleBlur(rowId, field);
      const nextRow = data[rowIndex + 1];
      const nextIsProductNameRow = nextRow.productName && nextRow.productName.trim() !== '';
      const nextRowFields = getEditableFields(nextIsProductNameRow);
      const targetField = nextRowFields.includes(field) ? field : nextRowFields[Math.min(currentFieldIndex, nextRowFields.length - 1)];
      setTimeout(() => handleCellClick(nextRow.id, targetField, true), 0);
    } else if (e.key === 'ArrowUp' && rowIndex > 0) {
      e.preventDefault();
      handleBlur(rowId, field);
      const prevRow = data[rowIndex - 1];
      const prevIsProductNameRow = prevRow.productName && prevRow.productName.trim() !== '';
      const prevRowFields = getEditableFields(prevIsProductNameRow);
      const targetField = prevRowFields.includes(field) ? field : prevRowFields[Math.min(currentFieldIndex, prevRowFields.length - 1)];
      setTimeout(() => handleCellClick(prevRow.id, targetField, true), 0);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Handle paste from Excel
  const handlePaste = useCallback((e) => {
    if (!editingCell || isReadOnly) return;
    
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const rows = pastedText.split('\n').filter(r => r.trim()).map(row => row.split('\t'));
    
    const [startRowId, startField] = editingCell.split('-');
    const startRowIndex = data.findIndex(r => r.id === startRowId);
    
    if (startRowIndex === -1) return;

    rows.forEach((row, rowOffset) => {
      row.forEach((value) => {
        const targetRowIndex = startRowIndex + rowOffset;
        if (targetRowIndex < data.length) {
          const targetRow = data[targetRowIndex];
          const numValue = parseFloat(value.replace(/[^0-9.-]/g, '')) || 0;
          onCellChange(targetRow.id, startField, numValue);
        }
      });
    });

    setEditingCell(null);
    setEditValue('');
    toast.success('Data pasted from clipboard');
  }, [editingCell, isReadOnly, data, onCellChange]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  // Determine which columns to show based on toggles
  const showProductNameColumns = !hideQtyRate;
  const showAmountColumns = !hideAmountRate;
  const showQtyColumns = !hideQtyRate;
  const showRateColumn = !hideAmountRate && !hideQtyRate;

  // Check if all rows are selected
  const allSelected = data.length > 0 && selectedRows.size === data.length;

  // Get error row IDs for highlighting
  const errorRowIds = new Set(validationErrors.map(e => e.rowId));

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div 
        ref={tableContainerRef}
        className="overflow-x-auto overflow-y-auto transition-all duration-200" 
        style={{ maxHeight: getTableMaxHeight(), scrollbarGutter: 'stable' }}
      >
        <table className="border-collapse w-full" style={{ minWidth: '1500px' }}>
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              {/* Checkbox column */}
              <th className="sticky left-0 z-20 bg-gray-100 border-r border-b border-gray-200 px-2 py-2 text-center" style={{ width: '40px', minWidth: '40px' }}>
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onSelectAll}
                  disabled={isReadOnly}
                />
              </th>
              <th className="sticky z-20 bg-gray-100 border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase whitespace-nowrap" style={{ left: '40px', width: '100px', minWidth: '100px' }}>Pipeline</th>
              <th className="sticky z-20 bg-gray-100 border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase whitespace-nowrap" style={{ left: '140px', width: '80px', minWidth: '80px' }}>Stage</th>
              <th className="sticky z-20 bg-gray-100 border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase whitespace-nowrap" style={{ left: '220px', width: '140px', minWidth: '140px' }}>Product Group</th>
              {showProductNameColumns && (
                <>
                  <th className="sticky z-20 bg-gray-100 border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase whitespace-nowrap" style={{ left: '360px', width: '120px', minWidth: '120px' }}>Product Name</th>
                  <th className="border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase bg-gray-50 whitespace-nowrap" style={{ width: '80px', minWidth: '80px' }}>List 1</th>
                  <th className="border-r border-b border-gray-200 px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase bg-gray-50 whitespace-nowrap" style={{ width: '80px', minWidth: '80px' }}>List 2</th>
                </>
              )}
              {showRateColumn && (
                <th className="border-r border-b border-gray-200 px-2 py-2 text-center text-[10px] font-semibold text-gray-600 uppercase bg-gray-50 whitespace-nowrap" style={{ width: '70px', minWidth: '70px' }}>Rate</th>
              )}
              {/* === QTY COLUMNS (all months) === */}
              {showQtyColumns && MONTHS.map(m => (
                <React.Fragment key={`qty-${m.key}`}>
                  {!hideLastYear && (
                    <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase bg-gray-100 whitespace-nowrap" style={{ width: '55px', minWidth: '55px' }}>LY {m.label} Qty</th>
                  )}
                  <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-blue-600 uppercase bg-blue-50 whitespace-nowrap" style={{ width: '55px', minWidth: '55px' }}>TY {m.label} Qty</th>
                </React.Fragment>
              ))}
              {showQtyColumns && !hideLastYear && (
                <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-gray-600 uppercase bg-gray-100 whitespace-nowrap" style={{ width: '65px', minWidth: '65px' }}>LY Total Qty</th>
              )}
              {showQtyColumns && (
                <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-blue-600 uppercase bg-blue-100 whitespace-nowrap" style={{ width: '65px', minWidth: '65px' }}>TY Total Qty</th>
              )}
              {/* === AMOUNT COLUMNS (all months) === */}
              {showAmountColumns && MONTHS.map(m => (
                <React.Fragment key={`amt-${m.key}`}>
                  {!hideLastYear && (
                    <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-gray-500 uppercase bg-gray-100 whitespace-nowrap" style={{ width: '70px', minWidth: '70px' }}>LY {m.label} Amt</th>
                  )}
                  <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-blue-600 uppercase bg-blue-50 whitespace-nowrap" style={{ width: '70px', minWidth: '70px' }}>TY {m.label} Amt</th>
                </React.Fragment>
              ))}
              {showAmountColumns && !hideLastYear && (
                <th className="border-r border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-gray-600 uppercase bg-gray-100 whitespace-nowrap" style={{ width: '80px', minWidth: '80px' }}>LY Total Amt</th>
              )}
              {showAmountColumns && (
                <th className="border-b border-gray-200 px-1 py-2 text-center text-[9px] font-semibold text-blue-600 uppercase bg-blue-100 whitespace-nowrap" style={{ width: '80px', minWidth: '80px' }}>TY Total Amt</th>
              )}
            </tr>
          </thead>
          <tbody>
            {visibleData.map((row, rowIndex) => {
              const isProductNameRow = row.productName && row.productName.trim() !== '';
              const isEmptyRow = row.isEmptyRow && !row.productGroup;
              const isSelected = selectedRows.has(row.id);
              const hasError = errorRowIds.has(row.id);
              
              // Determine row background color based on order frequency
              const getRowColorClass = () => {
                if (hasError) return 'bg-red-100';
                if (isSelected) return 'bg-blue-50';
                if (isEmptyRow) return 'bg-orange-50';
                // REPEAT DEAL rows get a special teal/emerald background to distinguish invoice-sourced data
                if (row.isRepeatDeal) return 'bg-emerald-50';
                switch (row.color) {
                  case 'green': return 'bg-green-50';
                  case 'yellow': return 'bg-yellow-50';
                  case 'red': return 'bg-red-50';
                  default: return isProductNameRow ? 'bg-white' : 'bg-amber-50';
                }
              };

              // Get left border indicator for visual distinction
              const getRowBorderClass = () => {
                if (row.isRepeatDeal) return 'border-l-4 border-l-emerald-500';
                return '';
              };

              const getTextColorClass = () => {
                if (hasError) return 'text-red-800';
                switch (row.color) {
                  case 'green': return 'text-green-800';
                  case 'yellow': return 'text-yellow-800';
                  case 'red': return 'text-red-800';
                  default: return 'text-gray-900';
                }
              };

              // Get pipeline badge color
              const getPipelineBadgeClass = () => {
                switch (row.pipeline) {
                  case 'NEW DEAL': return 'bg-blue-100 text-blue-700';
                  case 'REPEAT DEAL': return 'bg-green-100 text-green-700';
                  case 'WIN BACK': return 'bg-orange-100 text-orange-700';
                  case 'UP SELLING': return 'bg-purple-100 text-purple-700';
                  case 'CROSS SELLING': return 'bg-cyan-100 text-cyan-700';
                  default: return 'bg-gray-100 text-gray-700';
                }
              };
              
              return (
                <tr key={row.id} className={`hover:bg-gray-50/50 ${getRowColorClass()} ${getRowBorderClass()}`}>
                  {/* Checkbox */}
                  <td className="sticky left-0 border-r border-b border-gray-200 px-2 py-1.5 text-center bg-inherit" style={{ width: '40px', minWidth: '40px' }}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onRowSelect(row.id)}
                      disabled={isReadOnly}
                    />
                  </td>
                  <td className={`sticky border-r border-b border-gray-200 px-2 py-1.5 bg-inherit`} style={{ left: '40px', width: '100px', minWidth: '100px' }}>
                    {/* Show dropdown for user-added lines, badge for existing lines */}
                    {row.isNewLine && !isReadOnly ? (
                      <Select 
                        value={row.pipeline} 
                        onValueChange={(value) => onPipelineChange(row.id, value)}
                      >
                        <SelectTrigger className="h-7 text-[9px] bg-white border-gray-200">
                          <SelectValue>
                            <Badge className={`text-[9px] ${getPipelineBadgeClass()}`}>
                              {row.pipeline}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NEW DEAL">NEW DEAL</SelectItem>
                          <SelectItem value="CROSS SELLING">CROSS SELLING</SelectItem>
                          <SelectItem value="UP SELLING">UP SELLING</SelectItem>
                          <SelectItem value="WIN BACK">WIN BACK</SelectItem>
                          <SelectItem value="INDENTING DEAL">INDENTING DEAL</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`text-[9px] ${getPipelineBadgeClass()}`}>
                        {row.pipeline}
                      </Badge>
                    )}
                  </td>
                  <td className={`sticky border-r border-b border-gray-200 px-2 py-1.5 bg-inherit`} style={{ left: '140px', width: '80px', minWidth: '80px' }}>
                    <span className={`text-[9px] font-medium ${
                      row.stage === 'SUSPECT' || row.stage === 'SUSPECTS' ? 'text-yellow-700' :
                      row.stage === 'WON' ? 'text-green-700' :
                      row.stage === 'LOST' ? 'text-red-700' :
                      row.stage === 'CLOSING' ? 'text-emerald-700' :
                      row.stage === '-' ? 'text-gray-400' :
                      'text-gray-600'
                    }`}>{row.stage || '-'}</span>
                  </td>
                  <td className={`sticky border-r border-b border-gray-200 px-2 py-1.5 text-[11px] bg-inherit ${getTextColorClass()}`} style={{ left: '220px', width: '140px', minWidth: '140px' }}>
                    {/* REPEAT DEAL: Show static text, Others: Show dropdown */}
                    {row.pipeline === 'REPEAT DEAL' || row.isRepeatDeal ? (
                      <div className="flex items-center gap-1">
                        <Boxes className="h-3 w-3 text-amber-600" />
                        <span className="truncate">{row.productGroup || '-'}</span>
                      </div>
                    ) : (
                      <Select 
                        value={row.productGroup || ''} 
                        onValueChange={(value) => onProductGroupChange(row.id, value)}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger className={`h-7 text-[10px] ${(!row.productGroup || row.productGroup.trim() === '' || row.productGroup === 'NA' || row.productGroup === '-') ? 'bg-orange-100 border-orange-300' : 'bg-white border-gray-200'}`}>
                          <SelectValue placeholder="Select Group *">
                            {row.productGroup && row.productGroup !== 'NA' && row.productGroup !== '-' ? (
                              <div className="flex items-center gap-1">
                                {!isProductNameRow && <Boxes className="h-3 w-3 text-amber-600" />}
                                {row.productGroup}
                              </div>
                            ) : 'Select Group *'}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {productGroupOptions.map(pg => (
                            <SelectItem key={pg} value={pg}>{pg}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  {showProductNameColumns && (
                    <>
                      <td className={`sticky border-r border-b border-gray-200 px-2 py-1.5 text-[11px] bg-inherit ${getTextColorClass()}`} style={{ left: '360px', width: '120px', minWidth: '120px' }}>
                        {/* REPEAT DEAL: Show static text, Others: Show dropdown */}
                        {row.pipeline === 'REPEAT DEAL' || row.isRepeatDeal ? (
                          <span className="truncate">{row.productName || '-'}</span>
                        ) : row.productGroup && row.productGroup !== 'NA' && row.productGroup !== '-' ? (
                          <Select 
                            value={row.productName || ''} 
                            onValueChange={(value) => onProductNameChange(row.id, value)}
                            disabled={isReadOnly}
                          >
                            <SelectTrigger className={`h-7 text-[10px] ${(!row.productName || row.productName.trim() === '' || row.productName === 'NA' || row.productName === '-') ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                              <SelectValue placeholder="Select Product">
                                {row.productName && row.productName !== 'NA' && row.productName !== '-' ? row.productName : 'Select Product'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              {getProductsForGroup(row.productGroup).map(p => (
                                <SelectItem key={p.product_code || p.product_name} value={p.product_name}>{p.product_name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-gray-400 italic text-[10px]">-</span>
                        )}
                      </td>
                      <td className={`border-r border-b border-gray-200 px-2 py-1.5 text-[11px] ${row.color === 'default' ? 'text-gray-600' : getTextColorClass()}`}>
                        {row.pipeline !== 'REPEAT DEAL' && !row.isRepeatDeal && row.productName && row.productName.trim() !== '' && row.productName !== 'NA' ? (
                          (() => {
                            const opts = getList1ForProduct(row.productGroup, row.productName);
                            return opts.length > 0 ? (
                              <Select value={row.list1 || ''} onValueChange={(v) => onList1Change(row.id, v)} disabled={isReadOnly}>
                                <SelectTrigger className="h-6 text-[10px] bg-white border-gray-200">
                                  <SelectValue placeholder="-">{row.list1 && row.list1 !== 'NA' ? row.list1 : '-'}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {opts.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-gray-400 italic text-[10px]">{row.list1 && row.list1 !== 'NA' ? row.list1 : '-'}</span>
                            );
                          })()
                        ) : (!row.list1 || row.list1.trim() === '' || row.list1 === 'NA' || row.list1 === '-') ? (
                          <span className="text-gray-400 italic text-[10px]">-</span>
                        ) : (
                          row.list1
                        )}
                      </td>
                      <td className={`border-r border-b border-gray-200 px-2 py-1.5 text-[11px] ${row.color === 'default' ? 'text-gray-600' : getTextColorClass()}`}>
                        {row.pipeline !== 'REPEAT DEAL' && !row.isRepeatDeal && row.productName && row.productName.trim() !== '' && row.productName !== 'NA' ? (
                          (() => {
                            const opts = getList2ForProduct(row.productGroup, row.productName, row.list1);
                            return opts.length > 0 ? (
                              <Select value={row.list2 || ''} onValueChange={(v) => onList2Change(row.id, v)} disabled={isReadOnly}>
                                <SelectTrigger className="h-6 text-[10px] bg-white border-gray-200">
                                  <SelectValue placeholder="-">{row.list2 && row.list2 !== 'NA' ? row.list2 : '-'}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {opts.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-gray-400 italic text-[10px]">{row.list2 && row.list2 !== 'NA' ? row.list2 : '-'}</span>
                            );
                          })()
                        ) : (!row.list2 || row.list2.trim() === '' || row.list2 === 'NA' || row.list2 === '-') ? (
                          <span className="text-gray-400 italic text-[10px]">-</span>
                        ) : (
                          row.list2
                        )}
                      </td>
                    </>
                  )}
                  {showRateColumn && (
                    <td className="border-r border-b border-gray-200 px-2 py-1.5 text-right">
                      <span className={`text-[11px] font-mono ${isProductNameRow ? getTextColorClass() : 'text-gray-400'}`}>
                        {row.rate?.toLocaleString('en-IN')}
                      </span>
                    </td>
                  )}
                  {/* === QTY CELLS (all months) === */}
                  {showQtyColumns && MONTHS.map(m => {
                    const qtyField = `${m.key}Qty`;
                    const lyQtyField = `ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Qty`;
                    const isProductNameRow_r = row.productName && row.productName.trim() !== '';
                    const qtyEditable = isProductNameRow_r && !isEmptyRow;
                    const qtyCellId = `${row.id}-${qtyField}`;
                    const isEditingQty = editingCell === qtyCellId;
                    return (
                      <React.Fragment key={`qty-${m.key}`}>
                        {!hideLastYear && (
                          <td className="border-r border-b border-gray-200 px-1 py-1 text-right bg-gray-50/50">
                            <span className="px-1 text-[10px] font-mono text-gray-500">
                              {row[lyQtyField] || 0}
                            </span>
                          </td>
                        )}
                        <td 
                          className={`border-r border-b border-gray-200 px-1 py-1 text-right ${
                            qtyEditable && !isReadOnly ? 'cursor-text hover:bg-blue-100 bg-blue-50/50' : 'bg-blue-50/30'
                          } ${isEditingQty ? 'bg-blue-100' : ''}`}
                          onClick={() => qtyEditable && handleCellClick(row.id, qtyField, true)}
                        >
                          {isEditingQty ? (
                            <input
                              ref={el => inputRefs.current[qtyCellId] = el}
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => handleBlur(row.id, qtyField)}
                              onKeyDown={(e) => handleKeyDown(e, row.id, qtyField, rowIndex)}
                              className="w-full px-1 py-0.5 text-[11px] font-mono text-right border-none focus:outline-none focus:ring-0 bg-blue-100"
                            />
                          ) : (
                            <span className={`px-1 text-[11px] font-mono ${qtyEditable ? 'text-blue-800 font-medium' : 'text-gray-400'}`}>
                              {row[qtyField] || 0}
                            </span>
                          )}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  {/* LY Total Qty */}
                  {showQtyColumns && !hideLastYear && (
                    <td className="border-r border-b border-gray-200 px-1 py-1 text-right bg-gray-100">
                      <span className="text-[10px] font-mono text-gray-600">
                        {row.lyTotalQty?.toLocaleString('en-IN') || 0}
                      </span>
                    </td>
                  )}
                  {/* TY Total Qty */}
                  {showQtyColumns && (
                    <td className="border-r border-b border-gray-200 px-1 py-1 text-right bg-blue-100">
                      <span className="text-[11px] font-mono font-semibold text-blue-900">
                        {row.totalQty?.toLocaleString('en-IN') || 0}
                      </span>
                    </td>
                  )}
                  {/* === AMOUNT CELLS (all months) === */}
                  {showAmountColumns && MONTHS.map(m => {
                    const amtField = `${m.key}Amount`;
                    const lyAmtField = `ly${m.key.charAt(0).toUpperCase() + m.key.slice(1)}Amount`;
                    const isProductNameRow_r = row.productName && row.productName.trim() !== '';
                    const amtEditable = !isProductNameRow_r && !isEmptyRow;
                    const amtCellId = `${row.id}-${amtField}`;
                    const isEditingAmt = editingCell === amtCellId;
                    return (
                      <React.Fragment key={`amt-${m.key}`}>
                        {!hideLastYear && (
                          <td className="border-r border-b border-gray-200 px-1 py-1 text-right bg-gray-50/50">
                            <span className="px-1 text-[10px] font-mono text-gray-500">
                              {formatIndianNumber(row[lyAmtField] || 0)}
                            </span>
                          </td>
                        )}
                        <td 
                          className={`border-r border-b border-gray-200 px-1 py-1 text-right ${
                            amtEditable && !isReadOnly ? 'cursor-text hover:bg-green-100 bg-green-50/50' : 'bg-green-50/30'
                          } ${isEditingAmt ? 'bg-green-100' : ''}`}
                          onClick={() => amtEditable && handleCellClick(row.id, amtField, true)}
                        >
                          {isEditingAmt ? (
                            <input
                              ref={el => inputRefs.current[amtCellId] = el}
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={() => handleBlur(row.id, amtField)}
                              onKeyDown={(e) => handleKeyDown(e, row.id, amtField, rowIndex)}
                              className="w-full px-1 py-0.5 text-[11px] font-mono text-right border-none focus:outline-none focus:ring-0 bg-green-100"
                            />
                          ) : (
                            <span className={`px-1 text-[11px] font-mono ${amtEditable ? 'text-green-800 font-medium' : 'text-gray-500'}`}>
                              {formatIndianNumber(row[amtField] || 0)}
                            </span>
                          )}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  {/* LY Total Amount */}
                  {showAmountColumns && !hideLastYear && (
                    <td className="border-r border-b border-gray-200 px-1 py-1 text-right bg-gray-100">
                      <span className="text-[10px] font-mono text-gray-600">
                        {formatLakhs(row.lyTotalAmount || 0)}
                      </span>
                    </td>
                  )}
                  {/* TY Total Amount */}
                  {showAmountColumns && (
                    <td className="border-b border-gray-200 px-1 py-1 text-right bg-green-100">
                      <span className="text-[11px] font-mono font-semibold text-green-900">
                        {formatLakhs(row.totalAmount || 0)}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleCount < data.length && (
          <div className="text-center py-3 text-xs text-gray-500 border-t border-gray-200 bg-gray-50">
            Showing {visibleCount} of {data.length} rows — scroll down to load more
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetEntry;
